import { cn } from "@/utils/theme";
import { Button, Icon } from "@elaniintc/goes-design-system";

type TableLeadingCellProps = {
  title: string;
  subtitle?: string;
  showButton?: boolean;
  onButtonClick?: () => void;
} & React.ComponentProps<"div">;

/**
 * Table leading cell component that displays text and an optional button.
 * This component is designed to be used in a table as first cell in every row.
 * NOTE: The button is hidden by default and only appears on row hover, "group" class is required in the cell container.
 *
 * @param text - The text to display in the cell
 * @param showButton - Whether to show the button
 * @param onButtonClick - Callback function for button click
 * @returns The table leading cell component
 */
const TableLeadingCell = ({
  title,
  subtitle,
  showButton = false,
  onButtonClick,
  className,
  ...props
}: TableLeadingCellProps) => {
  return (
    <div
      className={cn("flex items-center justify-between", className)}
      {...props}
    >
      <div className="flex flex-col justify-center gap-0 truncate">
        <span className="text-body-loud font-semibold truncate" title={title}>
          {title}
        </span>
        {subtitle && (
          <span
            className="text-body-quiet text-xs leading-5 tracking-wider truncate"
            title={subtitle}
          >
            {subtitle}
          </span>
        )}
      </div>
      {showButton && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remove"
          className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          onClick={onButtonClick}
        >
          <Icon type="far" name="arrow-up-right" />
        </Button>
      )}
    </div>
  );
};

export { TableLeadingCell, type TableLeadingCellProps };
